import { createSelector } from 'reselect';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import { ALL_TRAFFIC, DATASET } from '@utils/constants';
import createErrorCodeFilter from '@utils/filter/createErrorCodeFilter';
import stackBarChartOption, { series as defaultSeries } from '@utils/chart/stackBarChartOption';
import { getIntervalByGranularity } from '@utils/granularity';
import { granularity as getGranularity, colorRGBA, humanize } from '@utils';
import { getErrorMetricInfo } from '@utils/dimension';
import { filterColorSelector } from '../selectors';
import {
  currentErrorCodeSelector,
  errorCodesSelector,
  filterColorsSelector,
  metricSelector,
  filterSelector,
  intervalPayloadSelector,
} from '../base/selectors';
import { SCOPE } from './types';

const selector = (state) => get(state, SCOPE);

export default selector;

const barChartReqBody = {
  // modify properties, as required before making API request
  queries: [
    {
      type: 'timeseries',
      dataset: DATASET,
      metrics: ['RebufferingRatio', 'VideoStartFailures', 'VideoStartTime'],
      interval: '2018-12-19T22:35:18.907Z/2018-12-20T22:35:18.907Z',
      granularity: 'PT1H',
      filter: [],
      options: {
        withTotals: true,
      },
    },
  ],
};

export const totalAttemptsPayloadSelector: any = createSelector(
  metricSelector,
  intervalPayloadSelector,
  filterSelector,
  (metric, interval, filter) => {
    const payload = JSON.parse(JSON.stringify(barChartReqBody));
    const filterRules = filter.rule;

    const granularity = getGranularity({
      from: interval.from,
      to: interval.to,
      minuteGranularity: true,
    });
    const { from, to } = getIntervalByGranularity(interval, granularity);

    const errorMetricInfo = getErrorMetricInfo(metric);

    payload.queries[0].interval = `${from}/${to}`;
    payload.queries[0].metrics = [errorMetricInfo.baseline];
    payload.queries[0].granularity = granularity;
    payload.queries[0].filter = filterRules;

    return payload;
  },
);

export const selectedPayloadSelector = createSelector(
  intervalPayloadSelector,
  filterSelector,
  metricSelector,
  errorCodesSelector,
  (interval, filter, metric, errorCodes) => {
    const granularity = getGranularity({
      from: interval.from,
      to: interval.to,
      minuteGranularity: true,
    });
    const { from, to } = getIntervalByGranularity(interval, granularity);

    const errorMetricInfo = getErrorMetricInfo(metric);

    const filterRules = filter.rule;
    return errorCodes.map((errorCode) => {
      const payload = JSON.parse(JSON.stringify(barChartReqBody));
      payload.queries[0].interval = `${from}/${to}`;
      payload.queries[0].metrics = [errorMetricInfo.queryMetric];
      payload.queries[0].granularity = granularity;
      payload.queries[0].filter =
        filterRules.length > 0
          ? [...filterRules, [createErrorCodeFilter(metric, errorCode)]]
          : [[createErrorCodeFilter(metric, errorCode)]];
      return payload;
    });
  },
);

export const payloadsSelector = createSelector(
  totalAttemptsPayloadSelector,
  selectedPayloadSelector,
  (totalAttemptsPayload, selectedPayload) => [totalAttemptsPayload, ...selectedPayload],
);

export const chartDataSelector = createSelector(
  selector,
  filterColorSelector,
  errorCodesSelector,
  filterColorsSelector,
  currentErrorCodeSelector,
  (store, filterColor, errorCodes, filterColors, currentErrorCode) => {
    const payload = get(store, 'payload');
    const response = get(store, 'response');
    const currentErrorCodeIndex = errorCodes.findIndex(
      (errorCode) => errorCode === currentErrorCode,
    );
    const chartData: any = cloneDeep(stackBarChartOption);

    if (!Array.isArray(response) || !Array.isArray(payload)) {
      return null;
    }
    const seriesData = response.map((res) => {
      const list = get(res, '[0].data', []);
      return list.map((item) =>
        Object.keys(item).reduce((result: any, key) => {
          if (key === 'timestamp') {
            const timestamp = item[key];
            result.unshift(timestamp);
          } else {
            const metricValue = get(item, `${key}.${Object.keys(item[key])}`);
            result.push(metricValue || null);
          }
          return result;
        }, []),
      );
    });

    const countBarNumbers: any = [];

    const series = payload.map((query, index) => {
      const currentSeries: any = cloneDeep(defaultSeries);
      const metric = get(query, 'queries[0].metrics[0]', '');
      const errorCode = errorCodes[index - 1] || ALL_TRAFFIC;
      const name = metric === 'Attempts' ? metric : errorCode;
      const color = filterColors[index - 1];

      currentSeries.name = name;
      currentSeries.data = get(seriesData, `[${index}]`, []);
      currentSeries.itemStyle.color = color;

      countBarNumbers.push(currentSeries.data.length);

      if ((metric === 'Attempts' || metric === 'EndedPlays') && index === 0) {
        currentSeries.stack = null;
        currentSeries.itemStyle.borderColor = filterColor;
        currentSeries.itemStyle.borderWidth = 1;
        currentSeries.itemStyle.color = colorRGBA(filterColor, 0.2);
      } else if (errorCode !== currentErrorCode) {
        // Highlight current error code.
        currentSeries.itemStyle.color = colorRGBA(color, 0.2);
      }

      return currentSeries;
    });
    series[currentErrorCodeIndex + 1].zlevel = 1;
    chartData.series = series;
    const maxCountBarNumber = Math.max.apply(null, countBarNumbers);
    if (maxCountBarNumber < 4) {
      chartData.xAxis.splitNumber = maxCountBarNumber;
    }

    // set the yAxis min
    chartData.yAxis.min = 1;
    // since the type is 'log' and the min value is 1 we set up the 1 as 0 on the UI.
    // log(0) is -Infinity.
    // See the details https://github.com/apache/incubator-echarts/issues/9801
    chartData.yAxis.axisLabel.formatter = (value) =>
      value === 1 ? `${humanize(0, undefined)}` : `${humanize(value, undefined)}`;
    return chartData;
  },
);
